import { Box, Button, Container } from '@mui/material'
import { useOperationsStore } from '@operations/AppProvider'
import { StoppageReasonFormData } from '@operations/stores/StoppageReasonStore'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, useHistory } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'
import { StoppageReasonForm } from '../../features/stoppageReason/components/organisms/StoppageReasonForm'
import { useStoppageReasonMessageHandler } from '../../features/stoppageReason/hooks/useStoppageReasonMessageHandler'

export const StoppageReasonFormPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const stoppageReasonId = id ? Number(id) : undefined
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const history = useHistory()
  const navigate = useNavigate()

  const formId = 'stoppage-form'
  const { stoppageReasonStore, appStore } = useOperationsStore()
  const { getExceptionMessage, getSuccessMessage } = useStoppageReasonMessageHandler()

  useAsyncFetch(async () => {
    await stoppageReasonStore.fetchStoppageById(stoppageReasonId)
  }, [stoppageReasonStore, stoppageReasonId])

  const isEdit = !!stoppageReasonId
  const title = isEdit
    ? t('updateStopageReason', 'Update stoppage reason')
    : t('addNewStoppageReason', 'Add new stoppage reason')
  const breadcrumbs = [
    { label: t(PATHS.masterData.title), route: PATHS.masterData.url },
    {
      label: t(PATHS.masterData.stoppageReason.title),
      onClick: () => navigate(PATHS.masterData.stoppageReason.url),
    },
    {
      label: title,
    },
  ]

  const handleClose = () => {
    history.goBackHistoryOrDefault(PATHS.masterData.stoppageReason.url)
  }
  const handleSubmit = async (formData: StoppageReasonFormData) => {
    try {
      await stoppageReasonStore.onSubmit(formData)
      appStore.setShowAlert('success', getSuccessMessage(isEdit, formData.name))
      handleClose()
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        minHeight: '100%',
      }}
    >
      <Header
        title={title}
        position='relative'
        breadcrumbs={breadcrumbs}
        rightRenderOption={
          <>
            <Button size='large' color='inherit' onClick={handleClose} variant='text'>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              type='submit'
              form={formId}
              size='large'
              color='primary'
              variant='contained'
              data-cy='stoppage-reason-save-btn'
            >
              {t('submit', 'Submit')}
            </Button>
          </>
        }
      />
      <Container sx={{ padding: theme.customSpacing.m }}>
        <StoppageReasonForm
          stoppageReason={stoppageReasonStore.items.find(x => x.id === stoppageReasonId)}
          formId={formId}
          onSubmit={handleSubmit}
        />
      </Container>
    </Box>
  )
})
